/**
 * Client wrapper for POST /api/ai/chat
 * Reads x-claude-* token headers and dispatches "claude-usage" for QuotaContext / TokenUsageBadge.
 */

import { getOktaSessionSnapshot } from "../okta/oktaSessionStore.js";
import { incrementDailyAiRequests } from "../utils/aiDailyRequests.js";

/**
 * POST /api/ai/chat — sends conversation to Claude via the idwiz backend.
 * @param {object} payload
 */
export async function generateArchitectReply(payload) {
  // Cache the operations registry once per session (Week 1 metadata).
  if (import.meta.env.DEV && typeof window !== "undefined" && !window.__idwizOpRegistry) {
    fetch("/api/operations/registry", { credentials: "include" })
      .then((r) => r.json())
      .then((j) => {
        if (j?.ok && j.registry && typeof j.registry === "object") {
          window.__idwizOpRegistry = j.registry;
        }
      })
      .catch(() => {});
  }

  const snap = getOktaSessionSnapshot();
  const { orgUrl, apiToken } = snap ?? {};

  const pl = payload && typeof payload === "object" ? payload : {};
  const { _abortSignal, ...payloadRest } = pl;

  const body = {
    ...payloadRest,
    tenantContext: buildTenantContext(snap),
    okta: orgUrl && apiToken ? { orgUrl, apiToken } : pl.okta ?? null,
    oktaConnection:
      orgUrl && apiToken
        ? { orgUrl, apiToken }
        : pl.oktaConnection ?? null,
    project: pl.project ?? null,
    auditFindings: pl.auditFindings ?? null,
    driftChanges: pl.driftChanges ?? null,
  };

  const res = await fetch("/api/ai/chat", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    signal: _abortSignal instanceof AbortSignal ? _abortSignal : undefined,
  });

  const inputTokens = parseInt(res.headers.get("x-claude-input-tokens") ?? "0", 10);
  const outputTokens = parseInt(res.headers.get("x-claude-output-tokens") ?? "0", 10);
  const totalTokens = parseInt(res.headers.get("x-claude-total-tokens") ?? "0", 10);
  const cacheReadInputTokens = parseInt(res.headers.get("x-claude-cache-read-input-tokens") ?? "0", 10);
  const fromDevCache = res.headers.get("x-idwiz-dev-cache") === "hit";

  // Rate limit
  if (res.status === 429) {
    const retryAfter = parseInt(res.headers.get("retry-after") ?? "60", 10);
    const data = await res.json().catch(() => ({}));
    const err = new Error(data.message ?? "Rate limit reached. Please wait before sending another message.");
    err.code = "rate_limit";
    err.retryAfter = retryAfter;
    throw err;
  }

  // Server error
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    const err = new Error(data.message ?? `Server error ${res.status}`);
    err.code = data.error ?? "server_error";
    throw err;
  }

  if (totalTokens > 0 || cacheReadInputTokens > 0 || fromDevCache) {
    window.dispatchEvent(
      new CustomEvent("claude-usage", {
        detail: {
          inputTokens,
          outputTokens,
          totalTokens,
          cacheReadInputTokens,
          fromDevCache,
        },
      }),
    );
  }

  if (!fromDevCache) {
    incrementDailyAiRequests();
  }

  const data = await res.json();
  if (!data.ok) {
    const err = new Error(data.message ?? "Unknown error from idwiz backend");
    err.code = data.error ?? "unknown";
    throw err;
  }

  return data;
}

function buildTenantContext(snap) {
  if (!snap?.orgUrl) return null;
  const parts = [`Okta org: ${snap.orgUrl}`];
  if (snap.roles?.length) parts.push(`Admin roles: ${snap.roles.join(", ")}`);
  if (snap.capabilities?.tabAccess) {
    const tabs = Object.entries(snap.capabilities.tabAccess)
      .filter(([, v]) => v?.canRead || v?.canWrite)
      .map(([k, v]) => `${k}(${v.canWrite ? "rw" : "r"})`)
      .join(", ");
    if (tabs) parts.push(`Tab access: ${tabs}`);
  }
  return parts.join(" | ");
}
